import { debugLogger } from '@/utils/debugLogger';

/**
 * Validate image file before upload with mobile-friendly restrictions
 */
export const validateImageFile = (file: File): { valid: boolean; errorMessage?: string } => {
  // Check file size (max 10MB for better mobile compatibility)
  if (file.size > 10 * 1024 * 1024) {
    return {
      valid: false,
      errorMessage: "A imagem deve ter no máximo 10MB"
    };
  }

  // Only allow widely supported formats for mobile compatibility
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(file.type.toLowerCase())) {
    return {
      valid: false,
      errorMessage: "Apenas arquivos JPEG, PNG e WebP são permitidos"
    };
  }

  return { valid: true };
};

/**
 * Create object URL for preview
 */
export const createObjectURL = (file: Blob): string => {
  return URL.createObjectURL(file);
};

/**
 * Clean up object URLs to prevent memory leaks
 */
export const revokeObjectURL = (url: string): void => {
  URL.revokeObjectURL(url);
};

/**
 * Compress an image file to JPEG with specified quality
 * Always outputs a .jpg file for maximum compatibility
 * @param file Original image file
 * @param maxWidth Maximum width for the compressed image (maintains aspect ratio)
 * @param quality JPEG quality (0-1), where 1 is highest quality
 * @returns Promise resolving to a compressed File object with .jpg extension
 */
export const compressImageToJPEG = async (
  file: File, 
  maxWidth: number = 1200, 
  quality: number = 0.8
): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Create image object to load the file
    const img = new Image();
    img.src = URL.createObjectURL(file);
    
    img.onload = () => {
      // Calculate dimensions while maintaining aspect ratio
      let width = img.width;
      let height = img.height;
      
      if (width > maxWidth) {
        // Scale down while maintaining aspect ratio
        const ratio = maxWidth / width;
        width = maxWidth;
        height = Math.floor(height * ratio);
      }
      
      // Create canvas for drawing the resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      
      // Draw image on canvas
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      
      // Improve image quality for mobile
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert canvas to blob (compressed JPEG)
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to convert canvas to blob'));
            return;
          }
          
          // Create a new file from the blob with guaranteed .jpg extension
          const originalName = file.name.replace(/\.[^/.]+$/, ''); // Remove original extension
          const compressedFile = new File(
            [blob],
            `${originalName}.jpg`, // Always use .jpg extension
            {
              type: 'image/jpeg', // Always use JPEG MIME type
              lastModified: Date.now()
            }
          );
          
          debugLogger.debug('[MediaUtils] Image compressed:', {
            originalSize: file.size,
            compressedSize: compressedFile.size,
            originalType: file.type,
            finalType: compressedFile.type,
            originalName: file.name,
            finalName: compressedFile.name,
            compression: ((file.size - compressedFile.size) / file.size * 100).toFixed(1) + '%'
          });
          
          // Clean up object URL
          URL.revokeObjectURL(img.src);
          
          resolve(compressedFile);
        },
        'image/jpeg',
        quality
      );
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
  });
};

/**
 * Compress an image file as a blob with specified quality
 * Returns both the compressed blob and data URL for immediate preview
 * Always outputs JPEG format for maximum compatibility
 */
export const compressImageAsBlob = async (
  file: File,
  maxWidth: number = 1200,
  quality: number = 0.7
): Promise<{ blob: Blob; dataUrl: string }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    
    img.onload = () => {
      let width = img.width;
      let height = img.height;
      
      if (width > maxWidth) {
        const ratio = maxWidth / width;
        width = maxWidth;
        height = Math.floor(height * ratio);
      }
      
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      
      // Improve image quality
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);
      
      // Get data URL for immediate preview (always JPEG)
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to convert canvas to blob'));
            return;
          }
          
          URL.revokeObjectURL(img.src);
          resolve({ blob, dataUrl });
        },
        'image/jpeg', // Always output JPEG
        quality
      );
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };
  });
};
